import { useEffect } from "react";
import { useI18n } from "@/context/LangContext";

/**
 * Tawk.to live chat integration.
 * Reads property + widget ID from env (REACT_APP_TAWK_PROPERTY_ID, REACT_APP_TAWK_WIDGET_ID).
 * If either is missing, the script is NOT injected — the existing WhatsApp FAB
 * remains the chat channel. When configured, Tawk auto-picks visitor language,
 * so Arabic and French agent bots work out of the box.
 *
 * Setup (owner only, no code change needed):
 * 1) Create a Tawk.to property at https://dashboard.tawk.to
 * 2) Add REACT_APP_TAWK_PROPERTY_ID and REACT_APP_TAWK_WIDGET_ID to frontend/.env
 * 3) In Tawk dashboard → Administration → Language → enable Arabic + French bots
 * 4) Deploy. The widget appears bottom-left; WhatsApp FAB stays bottom-right.
 */
export default function TawkChat() {
  const { lang } = useI18n();
  const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
  const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID || "default";

  useEffect(() => {
    if (!propertyId) return;
    if (typeof window === "undefined") return;
    if (window.Tawk_API) return; // already loaded

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Tell Tawk the visitor's language and preferred WhatsApp fallback message
    window.Tawk_API.onLoad = function () {
      try {
        window.Tawk_API.setAttributes(
          { language: lang },
          function () { /* ack */ }
        );
      } catch (e) { /* noop */ }
    };

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    s.charset = "UTF-8";
    s.setAttribute("crossorigin", "*");
    s.setAttribute("data-testid", "tawk-script");
    document.body.appendChild(s);
    return () => {
      // Do not remove — Tawk manages its own lifecycle
    };
  }, [propertyId, widgetId, lang]);

  return null;
}
